import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { getImageUrl } from '../utils/imageUtils';
import { Shield, Check, X, AlertCircle, Image, User, Crown, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';

export default function ValidationPage() {
  const { user, api, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [pendingProfiles, setPendingProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [canVote, setCanVote] = useState(null);
  
  // Photo viewer state
  const [viewingPhotos, setViewingPhotos] = useState(null); // { photos: [], currentIndex: 0 }
  const [photoModalOpen, setPhotoModalOpen] = useState(false);

  useEffect(() => {
    // Refresh user data to ensure we have the latest ambassador status
    const initPage = async () => {
      await refreshUser();
      checkCanVote();
    };
    initPage();
  }, []);

  const checkCanVote = async () => {
    try {
      const response = await api.get('/validation/can-vote');
      setCanVote(response.data);
      if (response.data.can_vote) {
        fetchPendingProfiles();
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error checking vote eligibility:', error);
      setLoading(false);
    }
  };

  const fetchPendingProfiles = async () => {
    try {
      const response = await api.get('/validation/pending');
      setPendingProfiles(response.data);
    } catch (error) {
      if (error.response?.status !== 403) {
        toast.error('Erreur lors du chargement des profils');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (profileId, approved) => {
    setVoting(true);
    try {
      await api.post('/validation/vote', {
        profile_id: profileId,
        approved,
        rejection_reason: !approved ? rejectionReason : null
      });

      toast.success(approved ? 'Profil validé' : 'Profil refusé');
      setPendingProfiles(prev => prev.filter(p => p.user.id !== profileId));
      setSelectedProfile(null);
      setRejectionReason('');
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors du vote';
      toast.error(message);
    } finally {
      setVoting(false);
    }
  };

  // Photo viewer functions
  const openPhotoViewer = (photos, startIndex = 0) => {
    setViewingPhotos({ photos, currentIndex: startIndex });
    setPhotoModalOpen(true);
  };

  const nextPhoto = () => {
    if (viewingPhotos && viewingPhotos.currentIndex < viewingPhotos.photos.length - 1) {
      setViewingPhotos({ ...viewingPhotos, currentIndex: viewingPhotos.currentIndex + 1 });
    }
  };

  const prevPhoto = () => {
    if (viewingPhotos && viewingPhotos.currentIndex > 0) {
      setViewingPhotos({ ...viewingPhotos, currentIndex: viewingPhotos.currentIndex - 1 });
    }
  };

  const closePhotoViewer = () => {
    setPhotoModalOpen(false);
    setViewingPhotos(null);
  };

  // Wait for API response before showing any access message
  // This ensures we use fresh data from the server, not potentially stale context data
  if (canVote === null) {
    return (
      <div className="min-h-screen bg-dark pt-24 pb-12" data-testid="validation-page">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  // Not subscribed (using fresh API data)
  if (!canVote.is_subscribed) {
    return (
      <div className="min-h-screen bg-dark pt-24 pb-12" data-testid="validation-page">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="glass-card p-12">
            <Shield className="w-16 h-16 text-gold mx-auto mb-6" />
            <h1 className="font-serif text-3xl gold-gradient-text mb-4">
              Validation des membres
            </h1>
            <p className="text-gray-400 mb-2">
              Accédez à toutes les fonctionnalités de DiscreetLux
            </p>
            <p className="text-gray-500 text-sm mb-6">
              Photos, messagerie, chat en direct, recherche avancée...
            </p>
            <Button 
              onClick={() => navigate('/subscription')} 
              className="btn-gold"
            >
              Abonnez-vous
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Subscribed but not ambassador (using fresh API data)
  if (!canVote.can_vote && canVote.is_subscribed && !canVote.is_ambassador) {
    return (
      <div className="min-h-screen bg-dark pt-24 pb-12" data-testid="validation-page">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="glass-card p-12">
            <Crown className="w-16 h-16 text-gold mx-auto mb-6" />
            <h1 className="font-serif text-3xl gold-gradient-text mb-4">
              Réservé aux Ambassadeurs
            </h1>
            <p className="text-gray-400 mb-4">
              Vous êtes abonné, mais seuls les membres ayant obtenu le statut <strong className="text-gold">Ambassadeur</strong> peuvent valider les nouveaux profils.
            </p>
            <p className="text-gray-500 text-sm">
              Le statut Ambassadeur est attribué aux membres les plus actifs et engagés de la communauté.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark pt-24 pb-12" data-testid="validation-page">
      <div className="max-w-4xl mx-auto px-6">
        <div className="flex items-center gap-4 mb-8">
          <Shield className="w-8 h-8 text-gold" />
          <div>
            <h1 className="font-serif text-3xl gold-gradient-text">Validation des membres</h1>
            <p className="text-gray-400 text-sm">Examinez et validez les nouveaux profils</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
          </div>
        ) : pendingProfiles.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <Check className="w-12 h-12 text-green-400 mx-auto mb-4" />
            <p className="text-gray-400">Aucun profil en attente de validation</p>
            <p className="text-gray-500 text-sm mt-2">Revenez plus tard</p>
          </div>
        ) : (
          <div className="space-y-6" data-testid="pending-profiles">
            {pendingProfiles.map(({ user: profileUser, profile }) => {
              // Build all photos array for viewer
              const allPhotos = profile.photos?.map(p => p.url) || [];
              
              return (
              <div key={profileUser.id} className="glass-card p-6">
                <div className="flex flex-col md:flex-row gap-6">
                  {/* Photos */}
                  <div className="w-full md:w-64 flex-shrink-0">
                    <div 
                      className="aspect-square bg-dark-subtle rounded-sm overflow-hidden mb-4 cursor-pointer hover:ring-2 hover:ring-gold/50 transition-all"
                      onClick={() => allPhotos.length > 0 && openPhotoViewer(allPhotos, 0)}
                    >
                      {profile.profile_picture ? (
                        <img
                          src={`${getImageUrl(profile.profile_picture)}`}
                          alt={profile.pseudo}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <User className="w-16 h-16 text-gray-500" />
                        </div>
                      )}
                    </div>
                    
                    {profile.photos?.length > 0 && (
                      <div className="grid grid-cols-3 gap-2">
                        {profile.photos.slice(0, 6).map((photo, index) => (
                          <div 
                            key={index} 
                            className="aspect-square bg-dark-subtle rounded-sm overflow-hidden cursor-pointer hover:ring-2 hover:ring-gold/50 transition-all"
                            onClick={() => openPhotoViewer(allPhotos, index)}
                          >
                            <img
                              src={`${getImageUrl(photo.url)}`}
                              alt={`Photo ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                    
                    <div className="mt-4 flex items-center gap-2 text-sm text-gray-500">
                      <Image className="w-4 h-4" />
                      {profile.photos?.length || 0} photos
                    </div>
                  </div>

                  {/* Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-4">
                      <Link 
                        to={`/member/${profileUser.id}`}
                        className="font-serif text-xl text-white hover:text-gold transition-colors flex items-center gap-2"
                      >
                        {profile.pseudo}
                        <ExternalLink className="w-4 h-4 text-gold" />
                      </Link>
                      <span className="px-2 py-1 bg-dark-subtle text-gray-400 text-xs capitalize">
                        {profile.profile_type}
                      </span>
                    </div>

                    <div className="flex flex-wrap gap-3 text-sm text-gray-400 mb-4">
                      <span>{profile.age} ans</span>
                      <span>•</span>
                      <span>{profile.location}</span>
                      <span>•</span>
                      <span className="capitalize">{profile.body_type}</span>
                      <span>•</span>
                      <span className="capitalize">{profile.experience_level}</span>
                    </div>

                    <div className="mb-4">
                      <h4 className="text-gold text-sm uppercase tracking-widest mb-2">Description</h4>
                      <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">{profile.description}</p>
                    </div>

                    {profile.sexual_preferences?.length > 0 && (
                      <div className="mb-6">
                        <h4 className="text-gold text-sm uppercase tracking-widest mb-2">Préférences</h4>
                        <div className="flex flex-wrap gap-2">
                          {profile.sexual_preferences.map(pref => (
                            <span key={pref} className="px-2 py-1 bg-dark-subtle text-gray-400 text-xs">
                              {pref}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-4">
                      <Button
                        onClick={() => handleVote(profileUser.id, true)}
                        disabled={voting}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white flex items-center justify-center gap-2"
                        data-testid={`approve-${profileUser.id}`}
                      >
                        <Check className="w-4 h-4" />
                        Valider
                      </Button>

                      <Dialog>
                        <DialogTrigger asChild>
                          <Button
                            className="flex-1 bg-red-600/20 hover:bg-red-600/30 text-red-400 flex items-center justify-center gap-2"
                            data-testid={`reject-${profileUser.id}`}
                          >
                            <X className="w-4 h-4" />
                            Refuser
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-dark-paper border-white/10">
                          <DialogHeader>
                            <DialogTitle className="font-serif text-xl text-white">
                              Raison du refus
                            </DialogTitle>
                          </DialogHeader>
                          <div className="py-4">
                            <RadioGroup
                              value={rejectionReason}
                              onValueChange={setRejectionReason}
                              className="space-y-4"
                            >
                              <div className="flex items-center space-x-3">
                                <RadioGroupItem value="photos_not_good" id="photos" />
                                <Label htmlFor="photos" className="text-gray-300">
                                  Les photos ne sont pas appropriées
                                </Label>
                              </div>
                              <div className="flex items-center space-x-3">
                                <RadioGroupItem value="description_insufficient" id="description" />
                                <Label htmlFor="description" className="text-gray-300">
                                  La description est insuffisante
                                </Label>
                              </div>
                              <div className="flex items-center space-x-3">
                                <RadioGroupItem value="both" id="both" />
                                <Label htmlFor="both" className="text-gray-300">
                                  Les deux
                                </Label>
                              </div>
                            </RadioGroup>

                            <Button
                              onClick={() => handleVote(profileUser.id, false)}
                              disabled={!rejectionReason || voting}
                              className="w-full mt-6 bg-red-600 hover:bg-red-700 text-white"
                            >
                              {voting ? 'Envoi...' : 'Confirmer le refus'}
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                </div>
              </div>
            );
            })}
          </div>
        )}

        {/* Photo Viewer Modal */}
        <Dialog open={photoModalOpen} onOpenChange={setPhotoModalOpen}>
          <DialogContent className="bg-dark-paper border-white/10 max-w-4xl p-0">
            {viewingPhotos && (
              <div className="relative">
                {/* Main Image */}
                <div className="aspect-[4/3] bg-black flex items-center justify-center">
                  <img
                    src={`${getImageUrl(viewingPhotos.photos[viewingPhotos.currentIndex])}`}
                    alt={`Photo ${viewingPhotos.currentIndex + 1}`}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
                
                {/* Navigation */}
                {viewingPhotos.photos.length > 1 && (
                  <>
                    <button
                      onClick={prevPhoto}
                      disabled={viewingPhotos.currentIndex === 0}
                      className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                    <button
                      onClick={nextPhoto}
                      disabled={viewingPhotos.currentIndex === viewingPhotos.photos.length - 1}
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>
                  </>
                )}
                
                {/* Counter */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-3 py-1 bg-black/70 rounded-full text-white text-sm">
                  {viewingPhotos.currentIndex + 1} / {viewingPhotos.photos.length}
                </div>
                
                {/* Thumbnails */}
                {viewingPhotos.photos.length > 1 && (
                  <div className="flex gap-2 p-4 bg-dark-subtle overflow-x-auto">
                    {viewingPhotos.photos.map((photo, idx) => (
                      <button
                        key={idx}
                        onClick={() => setViewingPhotos({ ...viewingPhotos, currentIndex: idx })}
                        className={`w-16 h-16 flex-shrink-0 rounded overflow-hidden ${
                          idx === viewingPhotos.currentIndex ? 'ring-2 ring-gold' : 'opacity-60 hover:opacity-100'
                        }`}
                      >
                        <img
                          src={`${getImageUrl(photo)}`}
                          alt={`Thumbnail ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
